import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PushToast from './PushToast';

const MAX_TOASTS = 4;
const DURATION = 6000;

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

export function ToastProvider({ children }) {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);

  // toastsRef — синхронды ақиқат көзі. Рендр кезінде емес, әр өзгерістің өзінде
  // жаңартамыз: олай етпесек бір tick-та келген екі хабарламаның екіншісі
  // ескі ref-ті оқып, біріншісін жоғалтады.
  const toastsRef = useRef([]);
  const timersRef = useRef(new Map());
  const counterRef = useRef(0);

  const clearTimer = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissToast = useCallback((id) => {
    clearTimer(id);
    const next = toastsRef.current.filter((toast) => toast.id !== id);
    toastsRef.current = next;
    setToasts(next);
  }, [clearTimer]);

  // showToast — тұрақты (deps тұрақты). FCM мен socket бір хабарлама үшін
  // қатар келсе, бірдей dedupeKey арқылы дубль тасталады.
  const showToast = useCallback(({ title, body, image, route, dedupeKey }) => {
    const key = dedupeKey || `${route?.path || ''}|${title || ''}|${body || ''}`;
    const prev = toastsRef.current;
    if (prev.some((toast) => toast.dedupeKey === key)) {
      return;
    }
    const id = `toast-${++counterRef.current}`;
    let next = [...prev, { id, title, body, image, route, dedupeKey: key }];
    if (next.length > MAX_TOASTS) {
      const [evicted, ...rest] = next;
      clearTimer(evicted.id);
      next = rest;
    }
    toastsRef.current = next;
    timersRef.current.set(id, setTimeout(() => dismissToast(id), DURATION));
    setToasts(next);
  }, [clearTimer, dismissToast]);

  const handleClick = useCallback((id) => {
    const toast = toastsRef.current.find((item) => item.id === id);
    if (toast?.route) {
      if (toast.route.external) {
        window.open(toast.route.path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(toast.route.path);
      }
    }
    dismissToast(id);
  }, [navigate, dismissToast]);

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-6 right-6 z-[9995] flex max-md:bottom-[calc(72px+env(safe-area-inset-bottom))] flex-col gap-2">
        {toasts.map((toast) => (
          <PushToast
            key={toast.id}
            id={toast.id}
            title={toast.title}
            body={toast.body}
            image={toast.image}
            duration={DURATION}
            onClose={dismissToast}
            onClick={handleClick}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;